import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CheckCircle, XCircle, Info, X } from 'lucide-react';

/**
 * O AVISO CURTO NO RODAPÉ (o "toast"). Desde 23/10/2026 (item 8 da auditoria 3) é ele, e não o `alert()` do navegador — que no
 * aplicativo é a janela do sistema e trava a tela até alguém tocar em "OK".
 *
 * - Em componente: `useToast().success(...)` / `.error(...)`.
 * - Em qualquer lugar (função utilitária, contexto, rotina de download): `avisar(mensagem)`, `avisarErro(mensagem)`.
 * - No celular aparece embaixo, centrado, acima da área segura; no computador, no canto de baixo à direita.
 */
type Tipo = 'success' | 'error' | 'info';
const EVENTO_DO_AVISO = 'aviso:mostrar';
export function avisar(mensagem: string, tipo: Tipo = 'info'): void {
  window.dispatchEvent(new CustomEvent(EVENTO_DO_AVISO, { detail: { mensagem, tipo } }));
}
export const avisarErro = (mensagem: string) => avisar(mensagem, 'error');
export const avisarSucesso = (mensagem: string) => avisar(mensagem, 'success');

interface ToastMessage {
  id: number;
  type: Tipo;
  message: string;
}

interface ToastContextValue {
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextId = 0;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const addToast = useCallback((type: Tipo, message: string) => {
    const id = nextId++;
    setToasts(prev => [...prev.slice(-2), { id, type, message }]);
    // O erro fica um pouco mais: é o que a pessoa precisa ler até o fim.
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, type === 'error' ? 7000 : 4000);
  }, []);
  useEffect(() => {
    const ouvir = (e: Event) => { const d = (e as CustomEvent<{ mensagem: string; tipo: Tipo }>).detail; if (d?.mensagem) addToast(d.tipo ?? 'info', d.mensagem); };
    window.addEventListener(EVENTO_DO_AVISO, ouvir);
    return () => window.removeEventListener(EVENTO_DO_AVISO, ouvir);
  }, [addToast]);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const value: ToastContextValue = {
    success: useCallback((msg: string) => addToast('success', msg), [addToast]),
    error: useCallback((msg: string) => addToast('error', msg), [addToast]),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-[100] flex flex-col items-center gap-2 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] md:inset-x-auto md:right-4 md:items-end md:px-0" role="status" aria-live="polite" data-testid="avisos">
        {toasts.map(toast => (
          <div
            key={toast.id}
            data-testid="aviso"
            className={`pointer-events-auto flex w-full max-w-sm items-start gap-2 p-3 rounded-lg shadow-lg text-sm animate-[slideIn_0.3s_ease-out] ${
              toast.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : toast.type === 'error'
                ? 'bg-red-50 border border-red-200 text-red-800'
                : 'bg-gray-900 text-white'
            }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
            ) : toast.type === 'error' ? (
              <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            ) : (
              <Info className="h-5 w-5 text-white/80 flex-shrink-0" />
            )}
            <span className="flex-1">{toast.message}</span>
            <button
              onClick={() => removeToast(toast.id)}
              aria-label="Fechar o aviso"
              className="flex-shrink-0 text-gray-500 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
